import { BayLevelEnum, IBayLevelOffsetBottomBase } from "open-vessel-definition";
import {
  EInvalidContainerReasonEnum,
  IContainerInformationExtendedAndCGs,
  SmartCGs,
  TKeyPairedBaysLevelAndRow,
} from "./SmartCGs";
import { IBayPattern, ITierPattern } from "../../types/IPositionPatterns";

import { BayLcgVcgTcgAndPairings } from "../BayLcgVcgTcgAndPairings";
import { ICellCGsInfo } from "../CellInfo";
import { IContainerInformation } from "../../types/IContainerInformation";
import { calculateGeneralMedianVcg } from "./calculateMedianVcg";
import { createDictionary } from "@tedivo/tedivo-pure-helpers";

export function getContainersCgs<T extends IContainerInformation>(
  bayLcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings,
  containers: T[],
  options?: { verticalSeparation?: number },
  offsetBottomBases?: Array<IBayLevelOffsetBottomBase>,
): TGetContainersCgsResult {
  const { missingImportantLcgs, missingImportantVcgs, missingImportantTcgs } =
    bayLcgVcgTcgAndPairings;

  // SmartCGs will extend the containers and calculate the cgs
  const smartCGs = new SmartCGs(bayLcgVcgTcgAndPairings);
  const resultCGs = smartCGs.calculateCgsOfContainers(containers, {
    verticalSeparation: options?.verticalSeparation || 0,
  });

  // Convert the extended containers with cgs to cell info
  const containersCgsArray = resultCGs.cellsInfoCalculated.map(convertContainerExtWCgsToCellInfo);
  const containersCgsByPk = createDictionary(
    containersCgsArray,
    (container) => container.meta?.pk ?? "",
  );

  const containerCgsByBayAndLevelAndRow: Record<TKeyPairedBaysLevelAndRow, ICellCGsInfo[]> =
    convertContainerExtWCgsToCellInfoForBayAndLevelAndRow(
      resultCGs.containerCgsByBayAndLevelAndRow,
    );

  const { generalMedianVcg, midVcgByBay, midVcgByBayBlock, offsetBottomBasesDict } =
    calculateGeneralMedianVcg(
      resultCGs.bayLevelCgsInfo,
      missingImportantVcgs,
      offsetBottomBases || [],
    );

  return {
    missingImportantLcgs,
    missingImportantVcgs,
    missingImportantTcgs,

    containersCgsArray,
    containersCgsByPk,

    containerCgsByBayAndLevelAndRow,

    bayLevelCgsInfo: resultCGs.bayLevelCgsInfo,

    maxHeightAbsolute: resultCGs.maxHeight,
    maxHeightWithSeparationAbsolute: resultCGs.maxHeightWithSeparation,

    containersMissingCgsInfo: resultCGs.errors,

    stackBottomsByBay: resultCGs.stackBottomsByBay,

    generalMedianVcg,
    midVcgByBay,
    midVcgByBayBlock,
    offsetBottomBasesDict,

    smartCGs,

    lastUpdated: new Date(),
  };
}

export function convertContainerExtWCgsToCellInfo(
  container: IContainerInformationExtendedAndCGs,
): ICellCGsInfo {
  if ("invalidReason" in container) {
    throw new Error(`Container ${container.pk} is invalid: ${container.invalidReason}`);
  }

  const { cgs, bay, row, tier, iBay, level, size, pk, position, height, width, isReefer, isAbove } =
    container;
  return {
    size,
    lcg: cgs.lcg,
    tcg: cgs.tcg,
    bottomBase: cgs.bottomBase,
    bottomBaseWithSeparation: cgs.bottomBaseWithSeparation,
    bottomTier: cgs.bottomTier,
    tierBottomBase: cgs.tierBottomBase,
    extending: cgs.extending,
    isoOddBay: cgs.isoOddBay,
    bayLevel: cgs.bayLevel,
    meta: {
      pk,
      position,
      size,
      height,
      width,
      isReefer,
      isAbove,
      iBay,
      bay,
      level,
      row,
      tier,
    },
  };
}

function convertContainerExtWCgsToCellInfoForBayAndLevelAndRow(
  containerCgsByBayAndLevelAndRow: Record<
    TKeyPairedBaysLevelAndRow,
    IContainerInformationExtendedAndCGs[]
  >,
): Record<TKeyPairedBaysLevelAndRow, ICellCGsInfo[]> {
  const result: Record<TKeyPairedBaysLevelAndRow, ICellCGsInfo[]> = {};
  Object.entries(containerCgsByBayAndLevelAndRow).forEach(([key, value]) => {
    result[key as TKeyPairedBaysLevelAndRow] = value.map(convertContainerExtWCgsToCellInfo);
  });
  return result;
}

export interface IBayCgsInfo {
  [BayLevelEnum.ABOVE]?: IBayLevelCgsInfo;
  [BayLevelEnum.BELOW]?: IBayLevelCgsInfo;
}

interface IBayLevelCgsInfo {
  portTcg: number;
  starboardTcg: number;
  bottomBase: number;
  bottomTier: ITierPattern;
  // maxHeightOfBay: number;
  // maxHeightWithSeparationOfBay: number;
  maxHeightAbsoluteOfBay: number;
  maxHeightWithSeparationAbsoluteOfBay: number;
  maxForeLcg: number;
  minAftLcg: number;
  //positions: Set<IPositionPattern>;
}

export type TGetContainersCgsResult = {
  missingImportantLcgs: boolean;
  missingImportantVcgs: boolean;
  missingImportantTcgs: boolean;

  containersCgsArray: ICellCGsInfo[];
  containersCgsByPk: Record<string, ICellCGsInfo>;
  containerCgsByBayAndLevelAndRow: Record<TKeyPairedBaysLevelAndRow, ICellCGsInfo[]>;

  containersMissingCgsInfo: Map<string, EInvalidContainerReasonEnum>;

  generalMedianVcg: number;

  midVcgByBay: Record<IBayPattern, number>;
  midVcgByBayBlock: Record<IBayPattern | `${IBayPattern}-${IBayPattern}`, number>;

  offsetBottomBasesDict: Record<string, number>;

  bayLevelCgsInfo: Record<IBayPattern | `${IBayPattern}-${IBayPattern}`, IBayCgsInfo>;

  maxHeightAbsolute: number;
  maxHeightWithSeparationAbsolute: number;

  stackBottomsByBay: Record<`${IBayPattern}-${BayLevelEnum.ABOVE | BayLevelEnum.BELOW}`, number>;

  smartCGs: SmartCGs;

  lastUpdated: Date;
};
